import { Injectable, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ParamMapHelper } from '../helpers/param-map.helper';
import { StorageService } from './storage.service';
import { DateRangeScope } from '../types/date-range-scope.type';
import { DataAvailabilityService } from './data-availability.service';
import { DateRange } from '../types/date-range.type';
import { NavigationService } from './navigation.service';
import { DataAvailabilityInfo } from '../types/data-availability-info.type';
import { DateHelper, DateRangeFormat } from '../helpers/date.helper';
import { ApiService } from './api/api.service';
import { SharedTimeUpdateService } from './shared-time-update.service';

@Injectable({ providedIn: 'root' })
export class CalendarService {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly storageService = inject(StorageService);
  private readonly dataAvailabilityService = inject(DataAvailabilityService);
  private readonly navigationService = inject(NavigationService);
  private readonly apiService = inject(ApiService);
  private readonly sharedTimeUpdateService = inject(SharedTimeUpdateService);

  private dateRangeScope: DateRangeScope = DateRangeScope.Global;
  private getDatesWithData: () => Promise<DateRange[]>;
  private getDataAvailabilityInfo: () => Promise<DataAvailabilityInfo>;
  private _dateRange: DateRange;
  private _dataAvailability: DataAvailabilityInfo;
  private dateRangeChangedSubject = new Subject<DateRange>();
  private dateChangedSubject = new Subject<Date>();
  private dataAvailabilityChangedSubject = new Subject<DataAvailabilityInfo>();
  private today: Date;

  get dateRange(): DateRange {
    return this._dateRange;
  }

  get fullDateRange(): DateRange {
    return new DateRange(DateHelper.startOfDay(this._dateRange.start), DateHelper.endOfDay(this._dateRange.end));
  }

  get date(): Date {
    return this._dateRange?.start;
  }

  get dateRangeChanged$(): Observable<DateRange> {
    return this.dateRangeChangedSubject.asObservable();
  }

  get dateChanged$(): Observable<Date> {
    return this.dateChangedSubject.asObservable();
  }

  get dataAvailability(): DataAvailabilityInfo {
    return this._dataAvailability;
  }

  get dataAvailabilityChanged$(): Observable<DataAvailabilityInfo> {
    return this.dataAvailabilityChangedSubject.asObservable();
  }

  get dateRangeLabel(): string {
    if (this.dateRange == null) {
      return '';
    }
    return this.dateRange.isSingleDay() ? DateHelper.getDayLabel(this.dateRange.start) : this.dateRange.format(DateRangeFormat.DateOnly);
  }

  constructor() {
    this.sharedTimeUpdateService.timeUpdates$.pipe(takeUntilDestroyed(), switchMap(async () => await this.checkDateChange())).subscribe();
  }

  private async checkDateChange(): Promise<void> {
    const today = DateHelper.startOfToday();
    if (this.today == null) {
      return;
    }
    if (this.today.getTime() !== today.getTime()) {
      this.dataAvailabilityService.clear();
      await this.initializeCalendar(this.dateRangeScope, this.getDatesWithData, this.getDataAvailabilityInfo);
    }
  }

  public async initializeCalendar(
    dateRangeScope: DateRangeScope = DateRangeScope.Global,
    getDatesWithData?: () => Promise<DateRange[]>,
    getDataAvailabilityInfo?: () => Promise<DataAvailabilityInfo>,
    forcedDateRange?: DateRange,
  ): Promise<void> {
    this.today = DateHelper.startOfToday();
    if (this.dateRangeScope != dateRangeScope) {
      this.dateRangeScope = dateRangeScope;
      this._dateRange = null;
    }
    this.getDatesWithData = getDatesWithData;
    this.getDataAvailabilityInfo = getDataAvailabilityInfo;
    this._dataAvailability = await this.getDataAvailability();
    this.dataAvailabilityChangedSubject.next(this._dataAvailability);

    // Priority order: forcedDateRange > query params > storage > previous > data availability > today
    if (forcedDateRange?.isValid()) {
      await this.selectDateRange(forcedDateRange);
      return;
    }

    const queryDateRange = ParamMapHelper.parseInterval(this.activatedRoute.snapshot.queryParamMap);
    if (queryDateRange?.isValid()) {
      await this.selectDateRange(queryDateRange);
      return;
    }

    const storageDateRange = this.storageService.getDateRange(this.dateRangeScope);
    const dataAvailabilityDateRange = this._dataAvailability?.lastDate != null ? new DateRange(this._dataAvailability.lastDate) : null;
    const dateRange = storageDateRange ?? this._dateRange ?? dataAvailabilityDateRange ?? new DateRange(DateHelper.startOfToday());

    await this.selectDateRange(dateRange);
  }

  private async getDataAvailability(): Promise<DataAvailabilityInfo> {
    return this.getDatesWithData != null ? this.createDataAvailability(await this.getDatesWithData())
      : this.getDataAvailabilityInfo != null ? await this.getDataAvailabilityInfo()
      : this.dateRangeScope === DateRangeScope.DumpViewer ? this.createDataAvailability(await this.apiService.dumpViewer.getDatesWithData())
      : await this.dataAvailabilityService.getDataAvailability();
  }

  private createDataAvailability(datesWithData: DateRange[]): DataAvailabilityInfo {
    return new DataAvailabilityInfo(datesWithData, datesWithData.length > 0);
  }

  public async selectDateRange(dateRange: DateRange): Promise<void> {
    this._dateRange = dateRange;
    await this.navigationService.addQueryParams(ParamMapHelper.createIntervalParams(dateRange));
    this.storageService.setDateRange(this.dateRangeScope, dateRange);
    this.dateRangeChangedSubject.next(this.dateRange);
    this.dateChangedSubject.next(this.date);
  }
}
